import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core'
import { Editor, Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { Color } from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import OrderedList from '@tiptap/extension-ordered-list'
import BulletList from '@tiptap/extension-bullet-list'

export interface FontSizeOptions {
  types: string[]
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      /**
       * Set the font size
       */
      setFontSize: (size: number) => ReturnType
      /**
       * Unset the font size
       */
      unsetFontSize: () => ReturnType
    }
  }
}

const FontSize = Extension.create<FontSizeOptions>({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    }
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) =>
              element.style.fontSize.replace(/['"]+/g, ''),
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {}
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              }
            },
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) => {
          return chain()
            .setMark('textStyle', { fontSize: fontSize + 'px' })
            .run()
        },
      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain()
            .setMark('textStyle', { fontSize: null })
            .removeEmptyTextStyle()
            .run()
        },
    }
  },
})

@Component({
  selector: 'text-editor',
  templateUrl: './text-editor.component.html',
  styleUrls: ['./text-editor.component.css'],
})
export class TextEditorComponent implements OnInit, OnDestroy {
  @Input() public value: any
  @Input() public showHeading: boolean = true
  @Input() public showFontSize: boolean = false
  @Input() public showList: boolean = true
  @Output() public onChange: EventEmitter<any> = new EventEmitter()

  editor = new Editor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      OrderedList,
      BulletList,
      FontSize,
    ],
  })

  constructor() {}

  ngOnInit() {}

  ngOnDestroy(): void {
    this.editor.destroy()
  }

  public fontSizeChange(event) {
    const size = event.target.value
    const { view, state } = this.editor
    const { from, to } = view.state.selection
    const selected = state.doc.textBetween(from, to, '')

    if (selected) {
      this.editor
        .chain()
        .focus()
        .setFontSize(!!size ? parseInt(size) : null)
        .run()
    }
  }

  public get fontSize() {
    const attrs = this.editor.getAttributes('textStyle')
    const size = attrs.fontSize

    if (!!size) {
      return size.split('px')[0]
    }

    return null
  }
}
